import { Dispatch } from "redux";
import { InternalError } from "@redwit-commons/utils/exception2";
import {
  StateMachine3,
  transition,
  mkReducer,
  StateMachineAction,
} from "@redwit-react-commons/reducers/state3";
import { ParsedTaskObject } from "@redwit-commons/object/task";
import {
  AdminOrganizationAdmin,
  AdminOrganizationSummary,
} from "@goono-commons/admin/organization";
import { WorkspaceSummaryDto } from "@utils/types/workspace";
import { WorkspacesWithMembers } from "@utils/types/modal";
import {
  Workspace,
  WorkspacePlan,
  WorkspacePlanKind,
  WorkspacePlanServiceEnum,
} from "@goono-commons/v3/workspace";
import { GetWorkspaceListQueryParams } from "@react-query/workspace/useGetWorkspaceListQuery";
import {
  PurchasePlanDuration,
  RedwitDeal,
} from "@goono-commons/admin/redwitDeal";
import { Institution } from "@goono-commons/admin/institution";
import { AdminClient } from "@goono-commons/admin/client";
import {
  AdminWorkspace,
  AdminWorkspaceMemberWithLogData,
} from "@goono-commons/admin/workspace";
import { BulkUpdateRenewNotifyMailConfigMutationQueryOptions } from "@react-query/automatedMail/useBulkUpdateRenewNotifyMailConfigMutation";

export enum ModalStateStatus {
  INIT = "Modal::INIT",
}

export enum ModalActionKind {
  TRY_OPEN = "ModalAction::TRY_OPEN",
  TRY_CLOSE = "ModalAction::TRY_CLOSE",
  TRY_CLOSE_ALL = "ModalAction::TRY_CLOSE_ALL",
}

export enum ModalType {
  ORGANIZATION_ADMIN_CONFIG = "ModalType::ORGANIZATION_ADMIN_CONFIG",
  UPDATE_WORKSPACE_INFO = "ModalType::UPDATE_WORKSPACE_INFO",
  MANAGE_CLIENT = "ModalType::MANAGE_CLIENT",
  MANAGE_WORKSPACE_LIST = "ModalType::MANAGE_WORKSPACE_LIST",
  CLIENT_INFO = "ModalType::CLIENT_INFO",
  SEND_EMAIL = "ModalType::SEND_EMAIL",
  DELETE_WORKSPACE = "ModalType::DELETE_WORKSPACE",
  DELETE_WORKSPACE_MEMBER = "ModalType::DELETE_WORKSPACE_MEMBER",
  UPDATE_OWNER = "ModalType::UPDATE_OWNER",
  BULK_UPDATE_WORKSPACE_PLAN = "ModalType::BULK_UPDATE_WORKSPACE_PLAN",
  BULK_UPDATE_WORKSPACES_MEMBER = "ModalType::BULK_UPDATE_WORKSPACES_MEMBER",
  SET_MEMBER_PERMISSIONS = "ModalType::SET_MEMBER_PERMISSIONS",
  DELETE_OWNER_WARNING = "ModalType::DELETE_OWNER_WARNING",
  DELETE_ORGANIZATION = "ModalType::DELETE_ORGANIZATION",
  MANAGE_ORGANIZATION_ADMIN = "ModalType::MANAGE_ORGANIZATION_ADMIN",
  DELETE_ORGANIZATION_ADMIN = "ModalType::DELETE_ORGANIZATION_ADMIN",
  DELETE_WORKSPACE_MEMBER_ALERT = "ModalType::DELETE_WORKSPACE_MEMBER_ALERT",
  RECONFIRM = "ModalType::RECONFIRM",
  BULK_UPDATE_HISTORY = "ModalType::BULK_UPDATE_HISTORY",
  SET_DOWNLOAD_DATE = "ModalType::SET_DOWNLOAD_DATE",
  CONFIG_AUTOMATED_RENEW_NOTIFY_MAIL = "ModalType::CONFIG_AUTOMATED_RENEW_NOTIFY_MAIL",
  CREATE_DEAL = "ModalType::CREATE_DEAL",
  UPDATE_DEAL = "ModalType::UPDATE_DEAL",
  CREATE_WORKSPACE = "ModalType::CREATE_WORKSPACE",
  SELECT_INSTITUTION = "ModalType::SELECT_INSTITUTION",
}

export type ModalError = never;

export type OrganizationAdminConfigModalProps = {
  props:
    | {
        type: "create";
      }
    | {
        type: "update";
        organizationAdmin: AdminOrganizationAdmin;
      };
};

export type UpdateWorkspaceInfoModalProps = {
  workspace: AdminWorkspace;
};

export type ManageClientModalProps = {
  readonly ownWorkspace?: AdminWorkspace;
  readonly workspaceSummary?: WorkspaceSummaryDto;
  readonly planKind: WorkspacePlanKind | undefined;
  readonly workspaceMembers?: AdminWorkspaceMemberWithLogData[];
  readonly onConfirm: (params: {
    workspaceId?: string;
    assignedMembers?: AdminWorkspaceMemberWithLogData[];
    resignedMembers?: AdminWorkspaceMemberWithLogData[];
  }) => void;
};

export type ClientInfoModalProps = {
  readonly id: string;
  readonly pendingClientList: ParsedTaskObject[];
};

export type SendEmailModalProps = {
  readonly clientInfo: AdminClient;
  readonly pendingClients: ParsedTaskObject[];
};

export type DeleteWorkspaceModalProps = {
  readonly targetWorkspace: AdminWorkspace;
};

export type DeleteWorkspaceMemberModalProps = {
  readonly targetWorkspace: AdminWorkspace;
  readonly targetMember: AdminWorkspaceMemberWithLogData;
};

export type UpdateOwnerModalProps = {
  readonly targetClient: AdminClient;
  readonly service: WorkspacePlanServiceEnum;
};

export type BulkRenewWorkspacePlanModalProps = {
  readonly workspaceIdFilters: WorkspaceIdFilter;
  readonly workspaceListFilter: GetWorkspaceListQueryParams;
};

type WorkspaceIdFilter = {
  workspaceIds: string[];
  excludeIds: string[];
};

export type BulkUpdateWorkspacesMemberModalProps = {
  readonly workspaceIdFilters: WorkspaceIdFilter;
  readonly workspaceListFilter: GetWorkspaceListQueryParams;
  readonly workspaces: WorkspacesWithMembers;
};

export type SetMemberPermissionsModalProps = {
  readonly workspaceIdFilters: WorkspaceIdFilter;
  readonly workspaceListFilter: GetWorkspaceListQueryParams;
  readonly selectedMembers: AdminClient[];
};

export type DeleteOwnerWarningModalProps = {
  readonly confirm: () => void;
};

export type DeleteOrganizationModalProps = {
  readonly organization: AdminOrganizationSummary;
};

export type ManageOrganizationAdminModalProps = {
  readonly organizationAdmins?: AdminOrganizationAdmin[];
  readonly onConfirm: (newAdmins: AdminOrganizationAdmin[]) => void;
};

export type ManageWorkspaceListModalProps = {
  readonly workspaceList: Workspace[];
  readonly institutionId?: string;
  readonly showPrevWorkspaceList?: boolean;
  readonly onConfirm: (newWorkspaceList: Workspace[]) => void;
};

export type DeleteOrganizationAdminModalProps = {
  readonly organizationAdmin: AdminOrganizationAdmin;
};

export type DeleteWorkspaceMemberAlertModalProps = {
  readonly callback: () => void;
};

export type ReconfirmModalProps = {
  readonly title: string;
  readonly bodyTitle?: string;
  readonly bodyMsg: string;
  readonly confirmMsg: string;
  readonly reconfirmInputOption?: {
    answer: string;
  };
  readonly onConfirm: (() => void) | (() => Promise<void>);
};

export type SetDownloadDateModalProps = {
  readonly title: string;
  readonly cancelMsg?: string;
  readonly onConfirm: (params?: { from: string; to: string }) => void;
};

export type UpsertRenewNotifyMailConfigParams = {
  readonly workspaceId: string;
  readonly workspaceName: string;
};

export type BulkUpsertRenewNotifyMailConfigParams = {
  readonly queryOptions: BulkUpdateRenewNotifyMailConfigMutationQueryOptions;
  readonly selectedWorkspaceCount: number;
};

export type ConfigAutomatedRenewNotifyMailModalProps =
  | UpsertRenewNotifyMailConfigParams
  | BulkUpsertRenewNotifyMailConfigParams;

export type CreateDealModalConfirmParams = {
  plan: Omit<WorkspacePlan, "period"> & {
    duration: PurchasePlanDuration;
  };
  name?: string;
  manager?: string;
};

export type CreateDealModalProps = {
  readonly workspaceCount: number;
  readonly onConfirm: (params: CreateDealModalConfirmParams) => Promise<void>;
};

export type UpdateDealModalProps = {
  readonly initialDealInfo: RedwitDeal;
};

export type CreateWorkspaceModalProps = {
  readonly institutionId: string;
  readonly institutionName: string;
};

export type SelectInstitutionModalProps = {
  readonly onConfirm: (institution: Institution) => Promise<void>;
};

type GenericModalInfo<T = ModalType, U = any> = {
  readonly type: T;
} & U;

export type ModalInfo =
  | GenericModalInfo<
      ModalType.ORGANIZATION_ADMIN_CONFIG,
      OrganizationAdminConfigModalProps
    >
  | GenericModalInfo<
      ModalType.UPDATE_WORKSPACE_INFO,
      UpdateWorkspaceInfoModalProps
    >
  | GenericModalInfo<ModalType.MANAGE_CLIENT, ManageClientModalProps>
  | GenericModalInfo<
      ModalType.MANAGE_WORKSPACE_LIST,
      ManageWorkspaceListModalProps
    >
  | GenericModalInfo<ModalType.CLIENT_INFO, ClientInfoModalProps>
  | GenericModalInfo<ModalType.SEND_EMAIL, SendEmailModalProps>
  | GenericModalInfo<ModalType.DELETE_WORKSPACE, DeleteWorkspaceModalProps>
  | GenericModalInfo<
      ModalType.DELETE_WORKSPACE_MEMBER,
      DeleteWorkspaceMemberModalProps
    >
  | GenericModalInfo<ModalType.UPDATE_OWNER, UpdateOwnerModalProps>
  | GenericModalInfo<
      ModalType.BULK_UPDATE_WORKSPACE_PLAN,
      BulkRenewWorkspacePlanModalProps
    >
  | GenericModalInfo<
      ModalType.BULK_UPDATE_WORKSPACES_MEMBER,
      BulkUpdateWorkspacesMemberModalProps
    >
  | GenericModalInfo<
      ModalType.SET_MEMBER_PERMISSIONS,
      SetMemberPermissionsModalProps
    >
  | GenericModalInfo<
      ModalType.DELETE_OWNER_WARNING,
      DeleteOwnerWarningModalProps
    >
  | GenericModalInfo<
      ModalType.DELETE_ORGANIZATION,
      DeleteOrganizationModalProps
    >
  | GenericModalInfo<
      ModalType.MANAGE_ORGANIZATION_ADMIN,
      ManageOrganizationAdminModalProps
    >
  | GenericModalInfo<
      ModalType.DELETE_ORGANIZATION_ADMIN,
      DeleteOrganizationAdminModalProps
    >
  | GenericModalInfo<
      ModalType.DELETE_WORKSPACE_MEMBER_ALERT,
      DeleteWorkspaceMemberAlertModalProps
    >
  | GenericModalInfo<ModalType.RECONFIRM, ReconfirmModalProps>
  | GenericModalInfo<ModalType.BULK_UPDATE_HISTORY, {}>
  | GenericModalInfo<ModalType.SET_DOWNLOAD_DATE, SetDownloadDateModalProps>
  | GenericModalInfo<
      ModalType.CONFIG_AUTOMATED_RENEW_NOTIFY_MAIL,
      ConfigAutomatedRenewNotifyMailModalProps
    >
  | GenericModalInfo<ModalType.CREATE_DEAL, CreateDealModalProps>
  | GenericModalInfo<ModalType.UPDATE_DEAL, UpdateDealModalProps>
  | GenericModalInfo<ModalType.CREATE_WORKSPACE, CreateWorkspaceModalProps>
  | GenericModalInfo<ModalType.SELECT_INSTITUTION, SelectInstitutionModalProps>;

export type ModalState = {
  readonly status: ModalStateStatus.INIT;
  readonly openedModalList: ModalInfo[];
};

export type ModalAction =
  | ({
      readonly kind: ModalActionKind.TRY_OPEN;
    } & ModalInfo)
  | {
      readonly kind: ModalActionKind.TRY_CLOSE;
    }
  | {
      readonly kind: ModalActionKind.TRY_CLOSE_ALL;
    };

const smid = "MODAL_STATE_MACHINE3";
export type ModalStateMachineType = StateMachine3<
  ModalStateStatus,
  ModalState,
  ModalActionKind,
  ModalAction,
  ModalError
>;

export const modalStateMachine: ModalStateMachineType = new StateMachine3<
  ModalStateStatus,
  ModalState,
  ModalActionKind,
  ModalAction,
  ModalError
>(smid, { status: ModalStateStatus.INIT, openedModalList: [] }, [
  transition(
    ModalStateStatus.INIT,
    ModalStateStatus.INIT,
    ModalActionKind.TRY_OPEN
  ),
  transition(
    ModalStateStatus.INIT,
    ModalStateStatus.INIT,
    ModalActionKind.TRY_CLOSE
  ),
  transition(
    ModalStateStatus.INIT,
    ModalStateStatus.INIT,
    ModalActionKind.TRY_CLOSE_ALL
  ),
]);

export type DispatchModalAction = Dispatch<
  StateMachineAction<
    ModalStateStatus,
    ModalState,
    ModalActionKind,
    ModalAction,
    ModalError
  >
>;

export default mkReducer<
  ModalStateStatus,
  ModalState,
  ModalActionKind,
  ModalAction,
  ModalError
>(modalStateMachine);

export const doModalAction = (
  dispatch: DispatchModalAction,
  nextAction: ModalAction,
  onResolve: () => void = () => {},
  onReject: (err: ModalError | InternalError) => void = () => {}
) => {
  dispatch(modalStateMachine.newTryAction(nextAction, onResolve, onReject));
};

export const doModalActionAsync = (
  dispatch: DispatchModalAction,
  nextAction: ModalAction
) => {
  return new Promise<void>((resolve, reject) => {
    dispatch(modalStateMachine.newTryAction(nextAction, resolve, reject));
  });
};

export const resetModal = (dispatch: DispatchModalAction) => {
  dispatch(modalStateMachine.newResetAction());
};
